/* ============================================================
   ENGINE MODEL — the explicit, immutable playback state.
   ------------------------------------------------------------
   This is the formalisation of the ~20 hidden `R.*` fields the
   imperative executor mutates today (TvProvider). Folding them into
   one struct is what makes the engine unit-testable as a pure
   function. Grouped by concern; nothing here mirrors the player's
   buffer truth — that arrives in every event's `snapshot`.

   TARGET (Phase 4): this merges INTO `TvState` so there is ONE store.
   It lives separately during the incremental migration.
   ============================================================ */
import type { PlaybackPhase, ScreenMode } from '../types'
import type { TuneIntent } from '../cover'

export interface PlaybackModel {
  // --- intent (what the viewer asked for) ---
  wantLive: boolean
  manual: { index: number; atSec: number } | null
  paused: boolean

  // --- live tracking (ACQUIRE vs TRACK + catch-up) ---
  liveSynced: boolean // false = ACQUIRE (seek to live); true = TRACK (coast on stalls)
  liveLag: number // seconds behind real-live (TRACK only)
  lastReclaimedAdIndex: number | null
  lastFollowingAt: number
  lastSeekAt: number

  // --- transition / cover ---
  tune: TuneIntent | null // a channel tune in flight (owns the bumper cover)
  desiredIndex: number

  // --- program ident overlay ---
  identActive: boolean
  identItem: { scene: string; params: Record<string, unknown>; maxMs?: number } | null
  lastInterstitialVideo: string | null
  lastBumper: string | null
  /** Identity of the now-playing IDENT card currently shown (the lone overlay), or null. */
  shownKey: string | null
  /** Identity of the full-frame CONTENT presentable the Stage is showing (video | cover | ad | component),
   *  so PresentScene/PresentComponent/RevealVideo are emitted once per transition, not every tick. */
  contentKey: string | null

  // --- non-live ad auto-advance timing ---
  adStartedAt: number
  adStartedIndex: number | null
  lastEndedId: string | null
  // --- portion boundary (skip a removed gap / advance past content end), edge-triggered ---
  lastPortionAdvance: string | null

  // --- dead-video skip set ---
  deadIds: ReadonlySet<string>

  // --- last-emitted presentation (so SetScreen/SetPlayback/SetLive emit only on change) ---
  screen: ScreenMode
  playback: PlaybackPhase
  live: boolean
  screenInteractive: boolean // picture is ours-to-control (playing or we-paused) → click-catch on
  blocked: boolean // YouTube refusing playback systemically (IP bot-flag) → halt + drop shield
  consecutiveErrors: number // distinct-video failures with no genuine play between
}

export const initialPlaybackModel = (): PlaybackModel => ({
  wantLive: false,
  manual: null,
  paused: false,
  liveSynced: false,
  liveLag: 0,
  lastReclaimedAdIndex: null,
  lastFollowingAt: 0,
  lastSeekAt: 0,
  tune: null,
  desiredIndex: 0,
  identActive: false,
  identItem: null,
  lastInterstitialVideo: null,
  lastBumper: null,
  shownKey: null,
  contentKey: 'video', // the default presentable is the video frames (so a fresh video landing is a no-op)
  adStartedAt: 0,
  adStartedIndex: null,
  lastEndedId: null,
  lastPortionAdvance: null,
  deadIds: new Set<string>(),
  screen: 'off',
  playback: 'idle',
  live: false,
  screenInteractive: false,
  blocked: false,
  consecutiveErrors: 0,
})
